import { useState, FunctionComponent, JSX } from "react";
import { LoadingSpinner } from "src/components/generic";
import { useAuth } from "src/context/AuthContext";
import { ReactComponent as Logo } from "src/assets/logo-white.svg";
import { ReactComponent as CloseIcon } from "src/assets/close_icon.svg";
import { ReactComponent as HamburgerIcon } from "src/assets/hamburger-icon.svg";
import NavigationStateIcon from "./NavigationStateIcon";
import { format } from "src/utils";
import { Link } from "react-router-dom";

const SideNavigation: FunctionComponent = () => {
  const [open, setOpen] = useState<boolean>(true);
  const { user, logout } = useAuth();

  if (!user) return <LoadingSpinner />;

  const links = format.navigation.extractLinks(user.userType, open, logout);

  return (
    <nav
      className={`
        relative hidden lg:flex flex-col h-fill bg-primary text-white uppercase text-sm
        transition-all duration-500 ease-in-out shadow-subtle
        ${open ? "w-[230px] pl-8" : "w-[70px] items-center"}
        group
      `}
      data-cy="side-navigation"
    >
      {/* Header Section */}
      <div
        className={`flex justify-between items-start transition-all duration-500 ${
          open ? "flex-row" : "flex-col items-center"
        }`}
          >
              <Link to='/'>
        <div className="relative group/logo">
          <Logo
            className={`${open ? "pl-3" : "pl-0"} transition-all duration-500`}
            height={open ? "100px" : "40px"}
            width={open ? "100px" : "40px"}
          />
          {/* subtle glow when hovered */}
          <div className="absolute inset-0 rounded-full blur-md opacity-0 group-hover/logo:opacity-20 transition-opacity duration-700 bg-primary-light" />
        </div>
              </Link>

        <NavigationStateIcon
          Icon={open ? CloseIcon : HamburgerIcon}
          openState={open}
          toggleOpenState={setOpen}
        />
      </div>

      {/* Divider */}
      <div className="h-[1px] bg-secondary-light/40 my-3 w-full transition-all duration-500" />

      {/* Navigation Links */}
      <ul
        data-cy="navigation-links-list"
        className={`mt-4 flex flex-col gap-3 ${
          !open ? "pl-1 items-center" : "pl-2"
        }`}
      >
        {links.map((link: JSX.Element, index: number) => (
          <li
            key={index}
            className={`
              relative flex items-center justify-start
              p-2 rounded-md cursor-pointer select-none
              transition-all duration-300 ease-out
              hover:bg-primary-light/20
              hover:translate-x-0
              hover:shadow-strong
              before:absolute before:left-0 before:top-0 before:w-[3px] before:h-full before:bg-secondary
              before:scale-y-0 hover:before:scale-y-100 before:origin-top before:transition-transform before:duration-300
            `}
          >
            {link}
          </li>
        ))}
      </ul>

      {/* Subtle animated border accent */}
      <span className="absolute inset-y-0 left-0 w-[2px] bg-gradient-to-b from-secondary via-primary-light to-secondary animate-gradientMove opacity-80" />
    </nav>
  );
};

export default SideNavigation;
